import { useRef } from 'react'
import { SearchableSelectOptionsList } from './SearchableSelectOptionsList'
import { SelectButton } from './select/SelectButton'
import { SelectListboxShell } from './select/SelectListboxShell'
import { findSelectedOption } from './select/text'
import type { SelectOption, SelectSize } from './select/types'
import { useContainedBlur } from './select/useContainedBlur'
import { useSearchableSelectState } from './select/useSearchableSelectState'
import { useSelectTypeahead } from './useSelectTypeahead'

export type SearchableSelectOption = SelectOption

/**
 * Props for the searchable select component.
 */
export type SearchableSelectCustomProps = {
  /** Current selected option value. */
  value: string
  /** Options searched and rendered in the dropdown. */
  options: SearchableSelectOption[]
  /** Called when the selected option value changes. */
  onChange: (value: string) => void
  /** Called when focus leaves the control. */
  onBlur?: () => void
  buttonId?: string
  ariaLabel?: string
  ariaInvalid?: boolean
  describedById?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  disabled?: boolean
  size?: SelectSize
  emphasizePrimaryLabel?: boolean
  buttonClassName?: string
  optionsClassName?: string
}

const searchableDropdownPositionConfig = {
  widthStrategy: 'preferred',
  minMeasuredHeight: 220,
  minMaxHeight: 220,
  minViewportWidth: 220,
  preferredWidth: 320,
} as const

export const SearchableSelectCustom = ({
  value,
  options,
  onChange,
  onBlur,
  buttonId,
  ariaLabel,
  ariaInvalid = false,
  describedById,
  placeholder = '',
  searchPlaceholder = 'Search',
  emptyLabel = 'No results',
  disabled = false,
  size = 'md',
  emphasizePrimaryLabel = false,
  buttonClassName,
  optionsClassName,
}: SearchableSelectCustomProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const { query, setQuery, filteredOptions, resetQuery } = useSearchableSelectState(options)
  const { rootRef, handleBlur } = useContainedBlur(onBlur)
  const selectedOption = findSelectedOption(options, value)
  const selectedLabel = selectedOption?.label ?? placeholder
  const handleTypeahead = useSelectTypeahead({ options, value, onChange })

  return (
    <SelectListboxShell
      value={value}
      onChange={onChange}
      disabled={disabled}
      dropdownPositionConfig={searchableDropdownPositionConfig}
      rootRef={rootRef}
      onRootBlur={handleBlur}
      afterEnter={(updateDropdownPosition) => {
        updateDropdownPosition()
        searchInputRef.current?.focus()
      }}
      afterLeave={(finishDropdownLeave) => {
        finishDropdownLeave()
        resetQuery()
      }}
      renderButton={({ buttonRef }) => (
        <SelectButton
          buttonRef={buttonRef}
          buttonId={buttonId}
          ariaLabel={ariaLabel}
          ariaInvalid={ariaInvalid}
          describedById={describedById}
          onKeyDown={(event) => {
            if (event.metaKey || event.ctrlKey || event.altKey) return
            handleTypeahead(event.key)
          }}
          valueLabel={selectedLabel}
          selectedOption={selectedOption}
          size={size}
          emphasizePrimaryLabel={emphasizePrimaryLabel}
          buttonClassName={buttonClassName}
          chevronClassName="text-(--color-text-muted,#94a3b8)"
        />
      )}
      renderOptions={({ optionsRef, placement, style }) => (
        <SearchableSelectOptionsList
          filteredOptions={filteredOptions}
          query={query}
          setQuery={setQuery}
          searchInputRef={searchInputRef}
          searchPlaceholder={searchPlaceholder}
          emptyLabel={emptyLabel}
          emphasizePrimaryLabel={emphasizePrimaryLabel}
          optionsClassName={optionsClassName}
          placement={placement}
          style={style}
          optionsRef={optionsRef}
        />
      )}
    />
  )
}

export default SearchableSelectCustom
